import React, { useEffect, useState } from "react";
import { Download, X, Share, Plus } from "lucide-react";
import { canInstall, promptInstall } from "../lib/sw-register";

const LS_DISMISS = "eeman_install_dismissed";

function isIOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

function isStandalone() {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

export default function InstallAppBanner() {
  const [show, setShow] = useState(false);
  const [iosHint, setIosHint] = useState(false);
  const [showIOSGuide, setShowIOSGuide] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    if (localStorage.getItem(LS_DISMISS) === "1") return;

    if (isIOS()) {
      setIosHint(true);
      return;
    }

    if (canInstall()) setShow(true);
    const onCanInstall = () => setShow(true);
    window.addEventListener("eeman-can-install", onCanInstall);
    return () => window.removeEventListener("eeman-can-install", onCanInstall);
  }, []);

  const install = async () => {
    const ok = await promptInstall();
    if (ok) setShow(false);
  };

  const dismiss = () => {
    localStorage.setItem(LS_DISMISS, "1");
    setShow(false);
    setIosHint(false);
  };

  if (!show && !iosHint) return null;

  return (
    <>
      <div
        className="fixed bottom-3 left-3 right-3 lg:left-auto lg:right-6 lg:bottom-6 lg:w-80 z-40 border-2 border-sky-600 bg-white shadow-[4px_4px_0_0_#0284c7] p-4 flex items-start gap-3"
        data-testid="install-banner"
      >
        <div className="bg-sky-600 text-white p-2 shrink-0">
          <Download className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-bold text-sm">Install EEMAN MEATS</div>
          <div className="text-xs text-zinc-600 mt-0.5">
            {iosHint
              ? "Add to Home Screen for full-screen app experience"
              : "Get the app on your home screen — works offline"}
          </div>
          <div className="mt-2 flex gap-2">
            {show && (
              <button
                onClick={install}
                data-testid="install-app-btn"
                className="bg-sky-600 text-white px-3 py-1.5 text-xs uppercase tracking-wider hover:bg-sky-700"
              >
                Install Now
              </button>
            )}
            {iosHint && (
              <button
                onClick={() => setShowIOSGuide(true)}
                data-testid="install-ios-help-btn"
                className="bg-sky-600 text-white px-3 py-1.5 text-xs uppercase tracking-wider hover:bg-sky-700"
              >
                How
              </button>
            )}
            <button
              onClick={dismiss}
              data-testid="install-dismiss-btn"
              className="border border-zinc-300 px-3 py-1.5 text-xs uppercase tracking-wider hover:bg-zinc-50"
            >
              Later
            </button>
          </div>
        </div>
        <button
          onClick={dismiss}
          className="text-zinc-400 hover:text-zinc-900 p-0.5"
          aria-label="dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {showIOSGuide && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-4"
          onClick={() => setShowIOSGuide(false)}
          data-testid="install-ios-modal"
        >
          <div
            className="bg-white max-w-sm w-full p-5 border-2 border-sky-600"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="font-display font-bold text-lg">Install on iPhone</div>
              <button onClick={() => setShowIOSGuide(false)} className="text-zinc-400 hover:text-zinc-900">
                <X className="h-5 w-5" />
              </button>
            </div>
            <ol className="space-y-3 text-sm">
              <li className="flex items-start gap-2">
                <span className="bg-sky-600 text-white text-xs px-2 py-0.5 font-bold">1</span>
                <span>
                  Tap the <Share className="h-4 w-4 inline mx-1 text-sky-600" /> Share button at the bottom of Safari
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="bg-sky-600 text-white text-xs px-2 py-0.5 font-bold">2</span>
                <span>
                  Scroll down → tap <b>Add to Home Screen</b>{" "}
                  <Plus className="h-4 w-4 inline ml-1 text-sky-600" />
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="bg-sky-600 text-white text-xs px-2 py-0.5 font-bold">3</span>
                <span>Tap <b>Add</b> in the top-right corner</span>
              </li>
            </ol>
            <button
              onClick={() => setShowIOSGuide(false)}
              className="mt-4 w-full bg-sky-600 text-white py-2 text-sm uppercase tracking-wider hover:bg-sky-700"
            >
              Got It
            </button>
          </div>
        </div>
      )}
    </>
  );
}
